import React from 'react';
import { IconButton, Tooltip, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import moment from 'moment';
import 'moment/locale/pt-br';

import DefaultImage from 'images/defaultAndEmpties/defaultNoProductImageMediumRectangleLightGrayBg.svg';
import GroupedLabelsIcon from 'images/icons/labels/groupedLabels.svg';
import ClearedLabelIcon from 'images/icons/signals/removesAndDeletes/trashIconNavyBlueWhiteBg.svg';
import ClearedLabelIconDisabled from 'images/icons/signals/removesAndDeletes/trashIconLightGray.svg';

import {
  LabelProductBottomInfos,
  LabelProductTopInfos,
  LabelsCardDetails
} from '../../../products/styles';

import ValidationLengthCard from 'utils/ValidationLengthCard';

export default function LabelsCard({
  labelItem,
  onClear,
  selected,
  onToggleSelected
}) {
  if (!labelItem) return null;
  moment.locale('pt-br');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const TITLE_MAX_CHARS = isMobile ? 40 : 50;

  const userLocal = JSON.parse(localStorage.getItem('companies'));
  const currentUserName = userLocal?.user || 'Usuário';

  const rawTitleText =
    labelItem?.description || labelItem?.productDescription || 'PRODUTO';

  const conservationText =
    labelItem?.conservation && labelItem.conservation.id !== null
      ? (
          labelItem.conservation.abbreviation ||
          labelItem.conservation.description ||
          labelItem.conservation
        )
      : null;

  const titleText = `${rawTitleText}${
    conservationText ? ` (${String(conservationText).toUpperCase()})` : ''
  }`;

  const titleNode = ValidationLengthCard(titleText, TITLE_MAX_CHARS, {
    fontSize: '16px',
    className: 'title'
  });

  const qtyLabel = Number(labelItem?.quantity || 0)
    .toFixed(3)
    .replace('.', ',');

  const unitAbbr =
    labelItem?.consumptionUnit?.abbreviation ||
    labelItem?.consumptionUnit?.description ||
    labelItem?.unitAbbreviation ||
    'un';

  const imageUrl =
    labelItem?.image?.url || labelItem?.productImageUrl || DefaultImage;

  const cleared = Number(labelItem?.clearedCount ?? 0);
  const notCleared = Number(labelItem?.notClearedCount ?? 0);
  const totalGroup = cleared + notCleared;
  const showGroupedCard = totalGroup > 1;

  const manipulationAt =
    labelItem?.manipulation || labelItem?.manipulatedAt || labelItem?.createdAt;
  const validAt = labelItem?.validAt || labelItem?.expirationAt;

  const formatDateTime = (d) =>
    d ? moment(d).format('DD/MM/YYYY HH:mm') : '00/00/0000 00:00';

  const getStatusColor = (status) => {
    switch (status) {
      case 'VALID':
        return '#006608';
      case 'EXPIRED':
        return '#f53d4c';
      case 'ON_DATE':
        return '#aa8208';
      default:
        return '#000';
    }
  };

  const isClearedEnabled = !!labelItem?.isLabelCleared;

  const handleClearClick = (e) => {
    e.stopPropagation();
    if (!isClearedEnabled) return;
    if (typeof onClear === 'function') onClear(labelItem);
  };

  const toggleSelected = () => {
    if (typeof onToggleSelected === 'function') onToggleSelected();
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleSelected();
    }
  };

  return (
    <LabelsCardDetails
      $selected={selected}
      onClick={toggleSelected}
      role="button"
      tabIndex={0}
      onKeyDown={handleKey}
    >
      <LabelProductTopInfos>
        <div className="labelProductDescription mr8">
          <span className="title">{titleNode}</span>

          <div
            className="dFlex alignCenter justifyBetween"
            style={{ marginTop: 4 }}
          >
            <span className="lineValue mr4">
              QTDE:{' '}
              <span className="lineValue bold">
                {qtyLabel}
                {unitAbbr ? ` ${unitAbbr}` : ''}
              </span>
            </span>

            {showGroupedCard && (
              <div className="dFlex alignCenter">
                <img
                  src={GroupedLabelsIcon}
                  alt="Grouped labels"
                  style={{ height: 20, width: 20, marginRight: 4 }}
                />
                <span
                  className="bold"
                  style={{ fontSize: 12, color: '#0E1337' }}
                >
                  {notCleared}/{totalGroup}
                </span>
              </div>
            )}
          </div>
        </div>

        <img
          src={imageUrl}
          alt={
            labelItem?.description || labelItem?.productDescription || 'produto'
          }
          style={{ objectFit: 'cover' }}
          onError={(e) => {
            e.currentTarget.src = DefaultImage;
          }}
        />
      </LabelProductTopInfos>

      <LabelProductBottomInfos>
        <div className="bottomWrapper">
          <div className="bottomTexts">
            <span className="lineValue">
              Manipulação:{' '}
              <span className="lineValue">
                {formatDateTime(manipulationAt)}
              </span>
            </span>

            <span className="lineValue bold">
              Validade:{' '}
              <span
                className="lineValue"
                style={{
                  color: getStatusColor(labelItem?.labelStatus),
                  fontWeight: 'bold'
                }}
              >
                {formatDateTime(validAt)}
              </span>
            </span>

            <span className="lineValue">
              Responsável:{' '}
              <span className="lineValue">
                {labelItem?.user || currentUserName}
              </span>
            </span>
          </div>

          <Tooltip
            title={
              <div style={{ padding: 4 }}>
                <p style={{ margin: 0 }}>Efetuar Baixa</p>
              </div>
            }
            aria-label="efetuar-baixa"
            placement="left"
            disableHoverListener={!isClearedEnabled}
          >
            <span>
              <IconButton
                className="clearIconButton"
                disabled={!isClearedEnabled}
                onClick={handleClearClick}
              >
                <img
                  src={
                    isClearedEnabled
                      ? ClearedLabelIcon
                      : ClearedLabelIconDisabled
                  }
                  alt="Efetuar baixa"
                  className="clearIconImg"
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </LabelProductBottomInfos>
    </LabelsCardDetails>
  );
}
